

import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-lg bg-card text-card-foreground transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-card border-0",
        primary: "bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20",
        success: "bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent border-green-500/20", 
        warning: "bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border-amber-500/20",
        accent: "bg-gradient-to-br from-accent-blue/10 via-accent-purple/5 to-transparent border-accent-blue/20",
        destructive: "bg-gradient-to-br from-destructive/10 via-destructive/5 to-transparent border-destructive/20",
        glass: "bg-background/40 backdrop-blur-xl border border-border/20 hover:bg-background/50",
        gradient: "bg-gradient-to-br from-primary/20 via-accent-purple/10 to-accent-blue/20 border-primary/30",
        elevated: "bg-card border border-border/30 shadow-sm hover:shadow-md",
        outline: "bg-card border border-border/40 hover:border-border/60",
      },
      hover: {
        true: "hover:-translate-y-0.5 hover:shadow-sm cursor-pointer",
        false: "",
      },
      interactive: {
        true: "cursor-pointer group",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      hover: false,
      interactive: false,
    },
  }
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  icon?: ReactNode
  interactive?: boolean
}

const Card = forwardRef<
  HTMLDivElement,
  CardProps
>(({ 
  className, 
  variant, 
  hover,
  interactive,
  children,
  ...props 
}, ref) => (
  <div
    ref={ref}
    className={cn(
      cardVariants({ variant, hover, interactive }),
      className
    )}
    {...props}
  >
    {children}
  </div>
))
Card.displayName = "Card"

const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { icon?: ReactNode }
>(({ className, children, icon, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  >
    {icon && (
      <div className="mb-3 p-2 rounded-lg bg-primary/5 w-fit transition-colors group-hover:bg-primary/10">
        {icon}
      </div>
    )}
    {children}
  </div>
))
CardHeader.displayName = "CardHeader"

const CardTitle = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-xl font-semibold leading-tight tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
